// hooks/useTranslatedORFSettings.js
import { useState, useCallback, useEffect } from "react";

/* ------------------------------------------------------------
 * hard-coded defaults for every ORF-type (mirrors legacy page)
 * ---------------------------------------------------------- */
const ORF_DEFAULTS = {
  cds: { maxCds: "1", startInc: true, stopDec: true, maxCdsOverride: null },
  uorf: { maxCds: "0.5", startInc: true, stopDec: true },
  ouorf: { maxCds: "0.95", startInc: true, stopDec: true },
  odorf: { maxCds: "0.95", startInc: true, stopDec: true },
  dorf: { maxCds: "0.5", startInc: true, stopDec: true },
  noncoding: {
    maxCds: "0",
    startInc: true,
    stopDec: true,
    lowFD: false,
    highFD: true,
  },
  nested: {
    maxCds: "1",
    startInc: true,
    stopDec: true,
    lowFD: true,
    highFD: false,
  },
  extension: {
    maxCds: "0.5",
    startInc: true,
    stopDec: false,
    highFD: true,
    lowFD: false,
  },
  plusone: { maxCds: "0.5", startInc: false, stopDec: true },
  minusone: { maxCds: "0.5", startInc: false, stopDec: true },
  readthrough: { maxCds: "0.5", startInc: false, stopDec: true },
};

const defaultSettings = {
  orfType: "uorf",
  transcriptInput: "prin_trans",
  customTranscriptList: "",
  startCodons: ["AUG"],
  /* numeric filters */
  filters: {
    minCds: "0",
    maxCds: "1",
    minLen: "30",
    maxLen: "100000",
    minReadLen: "20",
    maxReadLen: "35",
    minProbability: "0.5",
    maxProbability: "1",
    minAvgReadDensity: "0",
    maxAvgReadDensity: "1000000"
  },
  emailNotification: false,
};

export const useTranslonExplorerSettings = () => {
  const [settings, setSettings] = useState(defaultSettings);

  /* ---------- public setters ---------- */
  const updateSetting = useCallback((key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  }, []);

  const updateMultipleSettings = useCallback((updates) => {
    setSettings((prev) => ({ ...prev, ...updates }));
  }, []);

  const resetSettings = useCallback(() => setSettings(defaultSettings), []);

  /* ---------- smart defaults on ORF-type change ---------- */
  useEffect(() => {
    const d = ORF_DEFAULTS[settings.orfType];
    if (!d) return; // safety

    setSettings((prev) => ({
      ...prev,

      /* filters */
      filters: {
        ...prev.filters,
        maxCds: d.maxCds ?? prev.filters.maxCds,
      },

      /* start-codon defaults: always AUG + NONE off */
      startCodons: ["AUG"],
    }));
  }, [settings.orfType]);

  /* ---------- validation ---------- */
  const validateSettings = useCallback(() => {
    const errors = [];
    if (!settings.orfType) errors.push("Please select an ORF type");
    if (settings.startCodons.length === 0)
      errors.push("Please select at least one start codon");

    if (
      settings.transcriptInput === "custom_trans" &&
      !settings.customTranscriptList.trim()
    )
      errors.push("Custom transcript list cannot be empty");

    return { isValid: errors.length === 0, errors };
  }, [settings]);

  return {
    settings,
    updateSetting,
    updateMultipleSettings,
    resetSettings,
    validateSettings,
  };
};
